import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import clm from 'country-locale-map'
import { useDebounce } from 'use-debounce'

import { COUNTRY_LANGUAGE_MAP } from '@/common/utils'

import { DEBOUNCE_DELAYS, DEFAULT_FORM_VALUES, FORM_FIELDS, getDefaultUnits } from '../constants/formConstants'

/**
 * Custom hook for managing organization creation form
 *
 * Handles form state, validation, and automatic field population based on country selection.
 * When a user selects a country, this hook automatically populates:
 * - Currency code (from country data)
 * - Language (from COUNTRY_LANGUAGE_MAP)
 * - Locale (from country default_locale)
 * - Unit system (metric/imperial based on country)
 *
 * @param {Object} currentUser - Current user object with firstName and lastName
 * @returns {{
 *   control: Object,
 *   formState: Object,
 *   getValues: Function,
 *   handleSubmit: Function,
 *   setValue: Function,
 *   trigger: Function,
 *   watch: Function,
 *   countryList: Array<Object>
 * }}
 *
 * @example
 * const { control, formState, handleSubmit, countryList } = useOrganizationForm(currentUser)
 */
export const useOrganizationForm = (currentUser) => {
    const { t } = useTranslation()

    // Memoize country list to prevent recreation on every render
    const countryList = useMemo(() => clm.getAllCountries(), [])

    const { control, formState, getValues, handleSubmit, setValue, trigger, watch } = useForm({
        mode: 'all',
        defaultValues: {
            ...DEFAULT_FORM_VALUES,
            [FORM_FIELDS.CONTACT_FIRST_NAME]: currentUser?.firstName || '',
            [FORM_FIELDS.CONTACT_LAST_NAME]: currentUser?.lastName || '',
        },
    })

    const organisationCountry = watch(FORM_FIELDS.ORGANISATION_COUNTRY)
    const [debouncedOrganisationCountry] = useDebounce(organisationCountry, DEBOUNCE_DELAYS.COUNTRY_CHANGE)

    // Auto-populate dependent fields when country changes
    useEffect(() => {
        if (debouncedOrganisationCountry) {
            const country = debouncedOrganisationCountry

            // Extract country data with fallbacks
            const currency = country?.currency || 'USD'
            const language = COUNTRY_LANGUAGE_MAP[country?.alpha2]?.locale || 'en-US'
            const unit = getDefaultUnits(country?.alpha2)
            const locale = country?.default_locale?.replace('_', '-') || 'en-US'

            // Update form fields
            setValue(FORM_FIELDS.ORGANISATION_CURRENCY_CODE, currency)
            setValue(FORM_FIELDS.ORGANISATION_LANGUAGE, language)
            setValue(FORM_FIELDS.ORGANISATION_LOCALE, locale)
            setValue(FORM_FIELDS.ORGANISATION_UNITS, unit)

            // Trigger validation for updated fields
            trigger()
        }
    }, [debouncedOrganisationCountry, setValue, trigger])

    return {
        control,
        formState,
        getValues,
        handleSubmit,
        setValue,
        trigger,
        watch,
        countryList,
    }
}
